import { call } from '@advanza/api'
import { Icon } from '@advanza/ui'
import Pagination from 'components/Pagination'
import React, { useEffect, useState } from 'react'

interface UninstallRow {
    service_provider_id: number
    business_name: string
    country_code: string | null
    platform: string
    reason: string | null
    detected_at: string | null
}

interface UninstallResponse {
    status?: string
    items?: UninstallRow[]
    count?: number
    error?: string
}

interface Props {
    refreshKey: number
}

const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [25, 50, 100]

const NativeAppUninstallTable = ({ refreshKey }: Props) => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [items, setItems] = useState<UninstallRow[]>([])
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [previousRefreshKey, setPreviousRefreshKey] = useState(refreshKey)

    useEffect(() => {
        const bypassCache = refreshKey !== previousRefreshKey
        setPreviousRefreshKey(refreshKey)
        loadPage(page, pageSize, bypassCache)
    }, [page, pageSize, refreshKey])

    const loadPage = async (nextPage: number, nextPageSize: number, bypassCache: boolean) => {
        setLoading(true)
        setError('')
        try {
            const params = new URLSearchParams()
            params.set('page', String(nextPage))
            params.set('pageSize', String(nextPageSize))
            if (bypassCache) params.set('bypass_cache', '1')
            // Cache-buster: keeps the URL unique so no CDN/browser cache can
            // serve a stale body for this live dashboard.
            params.set('_', String(Date.now()))

            const url = `office/misc/native-app-uninstalls?${params.toString()}`
            const response = (await call(url)) as UninstallResponse
            if (response?.status === 'success' && Array.isArray(response.items)) {
                setItems(response.items)
                setCount(response.count ?? 0)
            } else {
                setError(response?.error || 'Failed to load uninstalls')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const onPageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(event.target.value) || DEFAULT_PAGE_SIZE)
        setPage(0)
    }

    return (
        <div className="space-y-3">
            <p className="text-md text-neutral-500">
                Devices where a push token went dead (app uninstalled, notifications revoked, or
                token rotated). A strong per-user uninstall proxy, detected on the next push.
            </p>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                    {error}
                </div>
            )}

            <div className="relative max-h-[480px] overflow-y-auto overflow-x-auto rounded-lg border border-neutral-200">
                {loading && (
                    <div className="absolute inset-x-0 top-0 z-20 h-1 overflow-hidden bg-blue-100">
                        <div className="h-full w-1/3 animate-pulse bg-blue-500" />
                    </div>
                )}
                <table className="min-w-full divide-y divide-neutral-200 text-md">
                    <thead className="bg-neutral-50 sticky top-0 z-10">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold text-neutral-700 whitespace-nowrap">
                                Provider
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                Country
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                Platform
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-neutral-700 whitespace-nowrap">
                                Reason
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-neutral-700 whitespace-nowrap">
                                Detected
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white">
                        {items.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-4 py-6 text-center text-md text-neutral-500">
                                    No uninstalls detected yet
                                </td>
                            </tr>
                        )}
                        {items.map((m, i) => (
                            <tr
                                key={`${m.service_provider_id}-${m.detected_at}-${i}`}
                                className="hover:bg-neutral-50/80">
                                <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">
                                    {m.business_name}
                                </td>
                                <td className="px-4 py-3 text-center text-neutral-700">
                                    {m.country_code || '–'}
                                </td>
                                <td className="px-4 py-3 text-center text-neutral-700">
                                    {m.platform}
                                </td>
                                <td className="px-4 py-3 text-neutral-700">{m.reason || '–'}</td>
                                <td className="px-4 py-3 text-center text-neutral-700 whitespace-nowrap">
                                    {m.detected_at
                                        ? new Date(m.detected_at).toLocaleDateString()
                                        : '–'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-md text-neutral-600">
                    Rows per page
                    <span className="relative inline-flex items-center">
                        <Icon
                            name="keyboard_arrow_down"
                            fontSize={18}
                            className="pointer-events-none absolute left-1 text-neutral-500"
                        />
                        <select
                            value={pageSize}
                            onChange={onPageSizeChange}
                            className="appearance-none rounded-md border border-neutral-300 bg-white pl-7 pr-2 py-1 text-md cursor-pointer hover:border-neutral-400 focus:outline-none focus:ring-1 focus:ring-blue-500">
                            {PAGE_SIZE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </span>
                </label>
                <div className="min-w-[160px]">
                    <Pagination
                        current={page}
                        count={count}
                        pageSize={pageSize}
                        onPageChange={setPage}
                    />
                </div>
            </div>
        </div>
    )
}

export default NativeAppUninstallTable
